/**
 * Discord go-live connections to a community (ADR 0049).
 *
 * A community is a group of streamers, and most of them run their own
 * Discord. So a community has MANY connections, not one: anybody signed in
 * can connect a server they run, and each connection says whose go-lives it
 * wants. The owner of the community sees every connection and can remove any
 * of them; everybody else sees their own.
 *
 * WHY ANYBODY. The webhook URL is the proof of authority. Discord hands one
 * out only to somebody with Manage Webhooks on that server, so pasting one
 * shows you run that channel, and nobody can point tomo at a server that is
 * not theirs. What it announces — who went live — is public already.
 *
 * THE URL IS A CREDENTIAL. Anybody holding it can post in that channel, so it
 * goes in and never comes back out, not even to the person who pasted it.
 * A connection is identified by the webhook's id, which Discord does not
 * treat as secret; the token half of the URL is what is kept.
 */
import { z } from 'zod';

/**
 * Only Discord's own webhook endpoint, on Discord's own hosts.
 *
 * This is what stops the setting being a way to make tomo's servers request
 * an arbitrary address (the reason the roster feed table is code, not data).
 * The id is a snowflake; the token is Discord's, url-safe base64.
 */
export const DISCORD_WEBHOOK_URL =
  /^https:\/\/(?:(?:canary|ptb)\.)?discord(?:app)?\.com\/api(?:\/v\d+)?\/webhooks\/(\d{15,25})\/([A-Za-z0-9_-]{30,100})$/;

export const DiscordWebhookUrl = z
  .string()
  .trim()
  .regex(DISCORD_WEBHOOK_URL, 'Paste the webhook URL from Discord: Server Settings → Integrations → Webhooks → Copy Webhook URL.');

/** A connection's id: the Discord webhook's own id. */
export const DiscordHookId = z.string().regex(/^\d{15,25}$/, 'A Discord webhook id is a number.');

/** How many streamers one filter may name. More than a community has. */
export const DISCORD_FILTER_MAX = 200;

/**
 * Whose go-lives a connection posts.
 *
 * `except` is the common one for a streamer: their own server already
 * announces them, so they want everybody else. Logins are Twitch logins,
 * lower-cased; one that is not on the roster simply never matches.
 */
export const DiscordFilter = z.object({
  mode: z.enum(['all', 'except', 'only']),
  logins: z
    .array(z.string().trim().toLowerCase().regex(/^[a-z0-9_]{2,25}$/))
    .max(DISCORD_FILTER_MAX),
});

/**
 * Whether a post carries the stream's large preview image.
 *
 * On by default. Off leaves the streamer's name and picture, the title, the
 * game and the links: a compact card for a channel that gets a lot of
 * go-lives and would rather not scroll past a screenshot for each.
 */
export const DiscordShowPreview = z.boolean();

export const DiscordHookInput = z.object({
  webhook_url: DiscordWebhookUrl,
  filter: DiscordFilter.optional(),
  show_preview: DiscordShowPreview.optional(),
});

/** Change what a connection posts. Either or both; at least one. */
export const DiscordHookPatch = z
  .object({ filter: DiscordFilter.optional(), show_preview: DiscordShowPreview.optional() })
  .refine((p) => p.filter !== undefined || p.show_preview !== undefined, {
    message: 'Send a filter, show_preview, or both.',
  });

/**
 * `ok` posts. `gone` means Discord said the webhook no longer exists, so
 * nothing more is sent to it. `failing` is anything else, and posting goes on.
 */
export const DiscordHookStatus = z.enum(['ok', 'failing', 'gone']);

export const DiscordHook = z.object({
  id: DiscordHookId,
  room_id: z.string(),
  /** The webhook's name, as whoever made it set it in Discord. */
  webhook_name: z.string().nullable(),
  /** The Discord channel it posts in, as an id: the name needs a bot to read. */
  discord_channel_id: z.string().nullable(),
  filter: DiscordFilter,
  show_preview: DiscordShowPreview,
  status: DiscordHookStatus,
  last_error: z.string().nullable(),
  last_posted_at: z.string().datetime().nullable(),
  created_at: z.string().datetime(),
  /** Who connected it. Shown to the community's managers, who can remove it. */
  added_by: z.object({ uid: z.string(), handle: z.string().nullable() }),
  /** True when the caller connected it, and so may change its filter. */
  yours: z.boolean(),
});

export const DiscordHookList = z.object({
  data: z.array(DiscordHook),
  /** True when the caller manages the community and is seeing every connection. */
  all: z.boolean(),
});

/** What a test post did. */
export const DiscordHookTest = z.object({
  delivered: z.boolean(),
  status: DiscordHookStatus,
  detail: z.string().nullable(),
});

export type DiscordFilter = z.infer<typeof DiscordFilter>;
export type DiscordHookInput = z.infer<typeof DiscordHookInput>;
export type DiscordHookPatch = z.infer<typeof DiscordHookPatch>;
export type DiscordHookStatus = z.infer<typeof DiscordHookStatus>;
export type DiscordHook = z.infer<typeof DiscordHook>;
export type DiscordHookList = z.infer<typeof DiscordHookList>;
export type DiscordHookTest = z.infer<typeof DiscordHookTest>;
